import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { FilterPills } from '@/components/feature/FilterBar';

const categories = ['All', 'Study Tips', 'Exam Prep', 'Computer Science', 'News', 'Guides'];

const categoryColors: Record<string, string> = {
  'Study Tips': 'bg-emerald-50 text-emerald-700',
  'Exam Prep': 'bg-orange-50 text-orange-700',
  'Computer Science': 'bg-slate-100 text-slate-700',
  'News': 'bg-rose-50 text-rose-700',
  'Guides': 'bg-amber-50 text-amber-700',
};

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [blogPosts, setBlogPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/blogs`);
        if (res.ok) {
          const data = await res.json();
          // Transform API data to match frontend requirements if needed
          const transformed = data.map((b: any) => ({
            id: b.id || b._id,
            title: b.title,
            excerpt: b.excerpt || (b.content ? b.content.substring(0, 150) + '...' : ''),
            category: b.category || 'News',
            tags: b.tags,
            author: b.author,
            date: new Date(b.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }),
            readTime: Math.ceil((b.content || '').split(' ').length / 200) + ' min read',
            image: b.coverImage || 'https://readdy.ai/api/search-image?query=blog%20article%20educational&width=600&height=380',
            featured: b.status === 'published' && b.views > 100, // Example logic for featured
            slug: b.slug
          }));
          setBlogPosts(transformed);
        }
      } catch (error) {
        console.error('Failed to fetch blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [API_BASE]);

  const filtered = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter((p) => p.category === activeCategory || (p.tags && p.tags.includes(activeCategory)));

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero section*/}
      <section className="relative bg-slate-900 pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20background%20with%20open%20books%20and%20writing%20materials%2C%20deep%20navy%20gradient%2C%20geometric%20patterns%2C%20clean%20minimal%20academic%20design%2C%20subtle%20warm%20lighting%20on%20dark%20background%2C%20professional%20editorial%20atmosphere&width=1440&height=280&seq=bloghero1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">GCSE Computing</span>
            <i className="ri-arrow-right-s-line text-slate-500 text-xs"></i>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Blog</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            Insights, Tips &amp; Updates
          </h1>
          <p className="text-slate-400 text-base max-w-2xl">
            Expert advice on GCSE and A Level Computer Science revision, exam strategies and curriculum updates — written by teachers and learning specialists.
          </p>
        </div>
      </section>

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
          {loading && (
            <div className="text-center py-20 flex flex-col items-center gap-4">
              <i className="ri-loader-4-line animate-spin text-4xl text-emerald-600"></i>
              <p className="text-slate-400 text-sm font-medium">Fetching articles...</p>
            </div>
          )}

          {!loading && (
            <>
              {/* Category Filter */}
              <div className="mb-12">
                <FilterPills
                  value={activeCategory}
                  onChange={setActiveCategory}
                  options={categories.map((cat) => ({ value: cat, label: cat }))}
                />
              </div>

              {/* Post Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((post) => (
                  <article key={post.id} className="bg-white border border-slate-100 rounded-[2rem] overflow-hidden flex flex-col hover:border-emerald-200 hover:shadow-xl hover:shadow-slate-200/50 transition-all group">
                    <div className="w-full h-56 overflow-hidden relative">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className={`absolute top-4 left-4 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider ${categoryColors[post.category] || 'bg-slate-900 text-white'}`}>
                        {post.category}
                      </div>
                    </div>
                    <div className="p-7 flex flex-col flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 leading-tight group-hover:text-emerald-700 transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {post.title}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-1 line-clamp-3">{post.excerpt}</p>
                      <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                        <div className="flex items-center gap-4 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                          <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> {post.date}</span>
                          <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> {post.readTime}</span>
                        </div>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="w-10 h-10 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-emerald-600 hover:text-white transition-all cursor-pointer"
                        >
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="text-center py-20 text-slate-400">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-article-line text-3xl text-slate-200"></i>
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">No articles found</h3>
                  <p className="text-sm">We couldn't find any articles in this category.</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
